import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FaGlobe, 
  FaClock, 
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaPlus,
  FaExclamationTriangle,
  FaSync,
  FaPowerOff
} from 'react-icons/fa';
import siteService from '../services/siteService';
import changeService from '../services/changeService';

// 组件导入
import PageTitle from '../components/PageTitle';
import Card from '../components/Card';
import { ConfirmModal } from '../components/Modal';
import Alert from '../components/Alert';
import Spinner from '../components/Spinner';
import EmptyState from '../components/EmptyState';

// 工具函数导入
import { 
  formatDateTime, 
  formatRelativeTime,
  formatFrequency, 
  formatUrl,
  formatSiteStatus
} from '../utils/formatters';

/**
 * 站点详情页面 - 显示站点详细信息和监控页面列表
 */
const SiteDetail = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  
  // 状态管理
  const [site, setSite] = useState(null);
  const [recentChanges, setRecentChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showPageDeleteModal, setShowPageDeleteModal] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  const [pageDeleteLoading, setPageDeleteLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [toggleActiveLoading, setToggleActiveLoading] = useState(false);
  
  // 加载站点详情和最近的变更
  useEffect(() => {
    const fetchSiteDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 获取站点详情
        const siteData = await siteService.getSite(siteId);
        setSite(siteData);
        
        // 获取站点的最近变更记录
        const changesData = await changeService.getChanges({
          siteId: siteId,
          limit: 5,
          sortBy: 'changeDate',
          sortOrder: 'desc'
        });
        
        setRecentChanges(changesData.changes || []);
      } catch (err) {
        setError('加载站点详情失败: ' + err.message);
        console.error('Error fetching site details:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSiteDetails();
  }, [siteId]);
  
  // 处理删除站点
  const handleDeleteSite = async () => {
    try {
      setDeleteLoading(true);
      await siteService.deleteSite(siteId);
      setShowDeleteModal(false);
      navigate('/sites', { state: { message: '站点已成功删除' } });
    } catch (err) {
      setError('删除站点失败: ' + err.message);
      setShowDeleteModal(false);
      setDeleteLoading(false);
    }
  };
  
  // 处理删除页面
  const handleDeletePage = async () => {
    if (!pageToDelete) return;
    
    try {
      setPageDeleteLoading(true);
      await siteService.deletePage(siteId, pageToDelete.id);
      
      // 更新站点数据
      setSite(prevSite => ({
        ...prevSite,
        pages: prevSite.pages.filter(page => page.id !== pageToDelete.id)
      }));
      
      setShowPageDeleteModal(false);
      setStatusMessage({
        type: 'success',
        text: `页面 "${pageToDelete.name || pageToDelete.url}" 已成功删除`
      });
    } catch (err) {
      setError('删除页面失败: ' + err.message);
    } finally {
      setPageDeleteLoading(false);
      setPageToDelete(null);
    }
  };
  
  // 手动检查站点变更
  const handleCheckSite = async () => {
    try {
      setCheckingStatus(true);
      setStatusMessage({
        type: 'info',
        text: '正在检查站点变更，这可能需要一些时间...'
      });
      
      const result = await siteService.checkSite(siteId);
      
      setStatusMessage({
        type: 'success',
        text: `检查完成${result.changesDetected ? `，发现 ${result.changesDetected} 处变更` : '，未发现变更'}`
      });
      
      // 如果有变更，重新获取站点详情和变更记录
      if (result.changesDetected) {
        const siteData = await siteService.getSite(siteId);
        setSite(siteData);
        
        const changesData = await changeService.getChanges({
          siteId: siteId,
          limit: 5,
          sortBy: 'changeDate',
          sortOrder: 'desc'
        });
        
        setRecentChanges(changesData.changes || []);
      }
    } catch (err) {
      setStatusMessage({
        type: 'danger',
        text: '检查站点变更失败: ' + err.message
      });
    } finally {
      setCheckingStatus(false);
    }
  };
  
  // 切换站点监控状态
  const handleToggleActive = async () => {
    if (!site) return;
    
    try {
      setToggleActiveLoading(true);
      
      if (site.isActive) {
        await siteService.disableSite(siteId);
        setStatusMessage({
          type: 'success',
          text: '站点监控已停用'
        });
      } else {
        await siteService.enableSite(siteId);
        setStatusMessage({
          type: 'success',
          text: '站点监控已启用'
        });
      }
      
      // 更新站点状态
      setSite(prevSite => ({
        ...prevSite,
        isActive: !prevSite.isActive
      }));
    } catch (err) {
      setStatusMessage({
        type: 'danger',
        text: `${site.isActive ? '停用' : '启用'}站点监控失败: ${err.message}`
      });
    } finally {
      setToggleActiveLoading(false);
    }
  };
  
  // 渲染加载中状态
  if (loading) {
    return (
      <div className="py-5 text-center">
        <Spinner size="lg" text="加载站点详情..." />
      </div>
    );
  }
  
  // 渲染错误状态
  if (error) {
    return (
      <div className="py-4">
        <Alert type="danger" message={error} dismissible />
        <div className="mt-3 text-center">
          <Link to="/sites" className="btn btn-primary">
            返回站点列表
          </Link>
        </div>
      </div>
    );
  }
  
  // 如果未找到站点数据
  if (!site) {
    return (
      <div className="py-4">
        <EmptyState 
          type="error"
          title="站点不存在"
          message="未找到请求的站点信息"
          action={
            <Link to="/sites" className="btn btn-primary">
              返回站点列表
            </Link>
          }
        />
      </div>
    );
  }
  
  // 获取站点状态信息
  const statusInfo = formatSiteStatus(site.status);

  return (
    <div className="site-detail-page">
      {/* 页面标题 */}
      <PageTitle 
        title={site.name}
        subtitle={formatUrl(site.url)}
        breadcrumbs={[
          { path: '/sites', label: '监控站点' },
          { path: `/sites/${siteId}`, label: site.name }
        ]}
        actions={
          <>
            <button 
              className={`btn ${site.isActive ? 'btn-warning' : 'btn-success'}`}
              onClick={handleToggleActive}
              disabled={toggleActiveLoading}
            >
              {toggleActiveLoading ? (
                <span className="spinner-border spinner-border-sm me-2" />
              ) : (
                <FaPowerOff className="me-2" />
              )}
              {site.isActive ? '停用监控' : '启用监控'}
            </button>

            <button 
              className="btn btn-primary ms-2"
              onClick={handleCheckSite}
              disabled={checkingStatus}
            >
              {checkingStatus ? (
                <span className="spinner-border spinner-border-sm me-2" />
              ) : (
                <FaSync className="me-2" />
              )}
              立即检查
            </button>
            
            <Link to={`/sites/${siteId}/edit`} className="btn btn-secondary ms-2">
              <FaEdit className="me-2" />
              编辑站点
            </Link>
            
            <button 
              className="btn btn-danger ms-2"
              onClick={() => setShowDeleteModal(true)}
            >
              <FaTrash className="me-2" />
              删除站点
            </button>
          </>
        }
      />
      
      {/* 状态消息 */}
      {statusMessage && (
        <Alert 
          type={statusMessage.type} 
          message={statusMessage.text} 
          dismissible 
          onClose={() => setStatusMessage(null)}
          autoHideDuration={5000}
        />
      )}
      
      {/* 站点基本信息 */}
      <div className="row">
        <div className="col-lg-8">
          {/* 站点详情 */}
          <Card 
            title="站点信息" 
            icon={<FaGlobe />}
            className="mb-4"
          >
            <div className="row">
              <div className="col-md-6">
                <table className="table table-borderless">
                  <tbody>
                    <tr>
                      <th style={{ width: '40%' }}>站点URL:</th>
                      <td>
                        <a 
                          href={site.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-break"
                        >
                          {site.url}
                        </a>
                      </td>
                    </tr>
                    <tr>
                      <th>监控状态:</th>
                      <td>
                        <span className={`badge bg-${site.isActive ? 'success' : 'secondary'}`}>
                          {site.isActive ? '已启用' : '已停用'}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <th>运行状态:</th>
                      <td>
                        <span className={`badge bg-${statusInfo.className.replace('text-', '')}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="col-md-6">
                <table className="table table-borderless">
                  <tbody>
                    <tr>
                      <th style={{ width: '40%' }}>监控频率:</th>
                      <td>{formatFrequency(site.frequency)}</td>
                    </tr>
                    <tr>
                      <th>监控页面数:</th>
                      <td>{site.pages?.length || 0}</td>
                    </tr>
                    <tr>
                      <th>上次检查:</th>
                      <td>
                        {site.lastChecked ? (
                          <span title={formatDateTime(site.lastChecked, 'full')}>
                            {formatRelativeTime(site.lastChecked)}
                          </span>
                        ) : (
                          '从未检查'
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
          
          {/* 监控页面列表 */}
          <Card 
            title="监控页面" 
            icon={<FaEye />}
            actions={
              <Link to={`/sites/${siteId}/pages/add`} className="btn btn-sm btn-primary">
                <FaPlus className="me-2" />
                添加页面
              </Link>
            }
            className="mb-4"
          >
            {site.pages && site.pages.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>页面名称</th>
                      <th>URL</th>
                      <th>上次变更</th>
                      <th>状态</th>
                      <th className="text-end">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {site.pages.map(page => {
                      const pageStatus = formatSiteStatus(page.status);
                      
                      return (
                        <tr key={page.id}>
                          <td>
                            {page.name || '(未命名页面)'}
                          </td>
                          <td>
                            <a 
                              href={page.url} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-truncate d-inline-block"
                              style={{ maxWidth: '200px' }}
                            >
                              {formatUrl(page.url)}
                            </a>
                          </td>
                          <td>
                            {page.lastChanged ? (
                              <span title={formatDateTime(page.lastChanged, 'full')}>
                                {formatRelativeTime(page.lastChanged)}
                              </span>
                            ) : (
                              '从未变更'
                            )}
                          </td>
                          <td>
                            <span className={pageStatus.className}>
                              {pageStatus.label}
                            </span>
                          </td>
                          <td className="text-end">
                            <Link 
                              to={`/sites/${siteId}/pages/${page.id}/edit`}
                              className="btn btn-sm btn-outline-secondary me-2"
                            >
                              <FaEdit />
                            </Link>
                            <button 
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => {
                                setPageToDelete(page);
                                setShowPageDeleteModal(true);
                              }}
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState 
                type="no-data"
                title="暂无监控页面"
                message="您还没有添加任何要监控的页面"
                action={
                  <Link to={`/sites/${siteId}/pages/add`} className="btn btn-primary">
                    <FaPlus className="me-2" />
                    添加页面
                  </Link>
                }
              />
            )}
          </Card>
        </div>
        
        <div className="col-lg-4">
          {/* 最近变更 */}
          <Card 
            title="最近变更" 
            icon={<FaClock />}
            actions={
              <Link to={`/changes?siteId=${siteId}`} className="btn btn-sm btn-link">
                查看全部
              </Link>
            }
            className="mb-4"
          >
            {recentChanges.length > 0 ? (
              <div className="list-group list-group-flush">
                {recentChanges.map(change => (
                  <Link 
                    key={change.id} 
                    to={`/changes/${change.id}`}
                    className="list-group-item list-group-item-action"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-1 text-truncate" style={{ maxWidth: '250px' }}>
                        {change.summary || '页面内容已更新'}
                      </h6>
                      <small className={change.isRead ? 'text-muted' : 'text-primary fw-bold'}>
                        {!change.isRead && '新'}
                      </small>
                    </div>
                    <div className="d-flex justify-content-between">
                      <small className="text-muted">
                        {formatUrl(change.url)}
                      </small>
                      <small className="text-muted">
                        {formatRelativeTime(change.changeDate)}
                      </small>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <EmptyState 
                type="no-data"
                title="暂无变更记录"
                message="该站点暂无变更记录"
              />
            )}
          </Card>
          
          {/* 监控提示 */}
          {!site.isActive && (
            <Card className="bg-light mb-4">
              <div className="d-flex">
                <div className="flex-shrink-0 text-warning me-3">
                  <FaExclamationTriangle size={24} />
                </div>
                <div>
                  <h5>站点监控未启用</h5>
                  <p className="mb-2">当前站点的监控功能已停用，不会检测变更。</p>
                  <button 
                    className="btn btn-warning btn-sm"
                    onClick={handleToggleActive}
                    disabled={toggleActiveLoading}
                  >
                    {toggleActiveLoading && (
                      <span className="spinner-border spinner-border-sm me-2" />
                    )}
                    启用监控
                  </button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
      
      {/* 删除站点确认框 */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteSite}
        title="删除站点"
        confirmVariant="danger"
        confirmText="删除"
        confirmLoading={deleteLoading}
      >
        <div className="d-flex">
          <div className="flex-shrink-0 text-danger me-3">
            <FaExclamationTriangle size={24} />
          </div>
          <div>
            <p>您确定要删除站点 <strong>{site.name}</strong> 吗？</p>
            <p className="mb-0 text-danger">
              此操作将删除该站点的所有监控页面和变更记录，且<strong>无法恢复</strong>。
            </p>
          </div>
        </div>
      </ConfirmModal>
      
      {/* 删除页面确认框 */}
      <ConfirmModal
        isOpen={showPageDeleteModal}
        onClose={() => {
          setShowPageDeleteModal(false);
          setPageToDelete(null);
        }}
        onConfirm={handleDeletePage}
        title="删除监控页面"
        confirmVariant="danger"
        confirmText="删除"
        confirmLoading={pageDeleteLoading}
      >
        <div className="d-flex">
          <div className="flex-shrink-0 text-danger me-3">
            <FaExclamationTriangle size={24} />
          </div>
          <div>
            <p>您确定要删除页面 <strong>{pageToDelete?.name || pageToDelete?.url}</strong> 吗？</p>
            <p className="mb-0 text-danger">
              此操作将删除该页面的所有变更记录，且<strong>无法恢复</strong>。
            </p>
          </div>
        </div>
      </ConfirmModal>
    </div>
  );
};

export default SiteDetail; 